import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../../../components/base/PageLayout';

const Consulting: React.FC = () => {
  useEffect(() => {
    document.title = 'コンサルティング - 執事によるホテル・レストランのサービス品質向上支援｜執事ベル';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '執事ベルによるホスピタリティコンサルティングサービス。ホテル、旅館、レストラン、ウェディング施設など、接客サービス業のサービス品質向上を支援。現場診断、改善提案、スタッフ教育、マニュアル作成、VIP対応プログラム構築など、全日本執事協会頭取としての専門知識と経験を活かした実践的なコンサルティングを提供。顧客満足度向上とリピート率アップを実現します。');
    }
  }, []);

  const services = [
    {
      title: 'サービス品質向上',
      description: 'お客様満足度を最大化するサービス改善',
      features: [
        '現状分析・課題抽出',
        'サービス標準化',
        '品質管理システム構築',
        '継続的改善プロセス'
      ],
      icon: 'ri-star-line',
      duration: '3-6ヶ月',
      target: 'ホテル・レストラン・小売業'
    },
    {
      title: 'スタッフ教育プログラム',
      description: '従業員のホスピタリティマインド向上',
      features: [
        '教育カリキュラム設計',
        '講師育成プログラム',
        '評価システム構築',
        'フォローアップ体制'
      ],
      icon: 'ri-graduation-cap-line',
      duration: '6-12ヶ月',
      target: 'サービス業全般'
    },
    {
      title: 'オペレーション改善',
      description: '効率的で質の高いサービス提供体制の構築',
      features: [
        '業務フロー最適化',
        'マニュアル整備',
        'システム導入支援',
        'パフォーマンス測定'
      ],
      icon: 'ri-settings-3-line',
      duration: '4-8ヶ月',
      target: '宿泊・飲食・接客業'
    },
    {
      title: '顧客満足度向上',
      description: 'お客様の期待を超えるサービス体験の創造',
      features: [
        '顧客ニーズ分析',
        'サービス設計',
        'CRM戦略立案',
        'ロイヤルティ向上施策'
      ],
      icon: 'ri-heart-line',
      duration: '6-12ヶ月',
      target: '全業種対応'
    }
  ];

  const process = [
    {
      step: '01',
      title: '現状分析',
      description: '詳細なヒアリングと現場調査により、課題を明確化します',
      duration: '2-4週間'
    },
    {
      step: '02',
      title: '戦略立案',
      description: '分析結果を基に、具体的な改善戦略を策定します',
      duration: '2-3週間'
    },
    {
      step: '03',
      title: '実行支援',
      description: '戦略の実行を現場に寄り添いながらサポートします',
      duration: '3-9ヶ月'
    },
    {
      step: '04',
      title: '効果測定',
      description: '改善効果を測定し、継続的な向上をサポートします',
      duration: '継続'
    }
  ];

  const results = [
    {
      metric: '顧客満足度',
      improvement: '+25%',
      description: '平均的な改善効果'
    },
    {
      metric: 'リピート率',
      improvement: '+30%',
      description: '継続利用の向上'
    },
    {
      metric: '売上向上',
      improvement: '+20%',
      description: 'サービス改善による効果'
    },
    {
      metric: 'スタッフ満足度',
      improvement: '+35%',
      description: '働きがいの向上'
    }
  ];

  return (
    <PageLayout hero={<>
<section 
          className="relative py-32 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url('/images/generated/seq-consulting-hero-dec16b.jpg')`
          }}
        >
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center text-white">
              <h1 className="text-4xl md:text-6xl font-serif mb-6">
                コンサルティング
              </h1>
              <p className="text-xl md:text-2xl max-w-6xl mx-auto leading-relaxed">
                執事文化の研究を通じて学んだ<br />
                サービス業界専門コンサルティング
              </p>
            </div>
          </div>
        </section>
</>}>
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
        {/* Hero Section */}
        

        {/* Overview */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-serif text-gray-900 mb-6">
                  真のホスピタリティで<br />
                  企業価値を向上
                </h2>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  全日本執事協会頭取として、執事文化の本質を探求し続けています。その学びと実践を基に、サービス業界の企業様の課題解決をサポートいたします。
                </p>
                <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                  単なる理論ではなく、現場で培った実践的なノウハウにより、お客様満足度の向上と企業価値の最大化を目指します。
                </p>
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center p-4 bg-amber-50 rounded-lg">
                    <div className="text-2xl font-bold text-amber-700 mb-2">実践的</div>
                    <div className="text-sm text-gray-600">現場重視</div>
                  </div>
                  <div className="text-center p-4 bg-amber-50 rounded-lg">
                    <div className="text-2xl font-bold text-amber-700 mb-2">伴走型</div>
                    <div className="text-sm text-gray-600">継続サポート</div>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src="/images/generated/seq-consulting-overview-ac4380.jpg"
                  alt="コンサルティングの様子"
                  className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-serif text-gray-900 mb-4">
                コンサルティングサービス
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                企業様の課題に合わせた専門的なコンサルティング
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {services.map((service, index) => (
                <div key={index} className="bg-white rounded-lg shadow-lg p-8">
                  <div className="w-16 h-16 flex items-center justify-center mb-6 bg-amber-100 rounded-full">
                    <i className={`${service.icon} text-2xl text-amber-700`}></i>
                  </div>
                  <h3 className="text-2xl font-serif text-gray-900 mb-4">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                    <div>
                      <span className="font-medium text-gray-900">期間：</span>
                      <span className="text-gray-600">{service.duration}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-900">対象：</span>
                      <span className="text-gray-600">{service.target}</span>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="text-lg font-medium text-gray-900 mb-3">主なサービス</h4>
                    <ul className="space-y-2">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center text-gray-700">
                          <div className="w-2 h-2 bg-amber-500 rounded-full mr-3 flex-shrink-0"></div>
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-serif text-gray-900 mb-4">
                コンサルティングの流れ
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                体系的なアプローチで確実な成果を実現
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {process.map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-amber-700 text-white rounded-full">
                    <span className="text-xl font-bold">{step.step}</span>
                  </div>
                  <h3 className="text-xl font-serif text-gray-900 mb-4">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {step.description}
                  </p>
                  <div className="text-sm text-amber-700 font-medium">
                    {step.duration}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Results */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-serif text-gray-900 mb-4">
                目指す成果
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                コンサルティングを通じて実現を目指す価値
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                {
                  icon: 'ri-customer-service-2-line',
                  title: '顧客満足度向上',
                  description: 'お客様に感動を提供するサービスの実現'
                },
                {
                  icon: 'ri-line-chart-line',
                  title: 'リピート率向上',
                  description: '継続的な関係構築による安定成長'
                },
                {
                  icon: 'ri-trophy-line',
                  title: 'ブランド価値向上',
                  description: '企業イメージと信頼性の向上'
                },
                {
                  icon: 'ri-team-line',
                  title: 'スタッフ満足度向上',
                  description: '働きがいのある職場環境の実現'
                }
              ].map((result, index) => (
                <div key={index} className="text-center bg-white rounded-lg shadow-lg p-8">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-amber-100 rounded-full">
                    <i className={`${result.icon} text-2xl text-amber-700`}></i>
                  </div>
                  <h3 className="text-xl font-serif text-gray-900 mb-2">
                    {result.title}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {result.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Approach */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-serif text-gray-900 mb-4">
                コンサルティングの特徴
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                {
                  title: '現場に寄り添う姿勢',
                  description: '机上の理論ではなく、実際の現場の状況を深く理解し、その企業様に最適なソリューションをご提案します。執事文化の研究を通じて学んだ、細やかな観察力と先読みの力を活かします。',
                  icon: 'ri-hand-heart-line'
                },
                {
                  title: '継続的な改善支援',
                  description: '一時的な改善で終わらせず、継続的な成長をサポートする体制を構築します。共に学び、共に成長する姿勢を大切にしています。',
                  icon: 'ri-refresh-line'
                }
              ].map((feature, index) => (
                <div key={index} className="bg-white rounded-lg shadow-lg p-8">
                  <div className="w-16 h-16 flex items-center justify-center mb-6 bg-amber-100 rounded-full">
                    <i className={`${feature.icon} text-2xl text-amber-700`}></i>
                  </div>
                  <h3 className="text-xl font-serif text-gray-900 mb-4">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Case Studies */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-serif text-gray-900 mb-4">
                導入事例
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                {
                  industry: '高級ホテル',
                  challenge: 'ゲスト満足度の低下',
                  solution: 'スタッフ教育プログラムの刷新とサービス標準化',
                  result: '顧客満足度30%向上、リピート率25%向上',
                  duration: '6ヶ月'
                },
                {
                  industry: 'レストランチェーン',
                  challenge: '店舗間のサービス品質のばらつき',
                  solution: 'サービスマニュアル整備と継続的な品質管理システム構築',
                  result: '全店舗での均一なサービス品質実現、売上15%向上',
                  duration: '8ヶ月'
                }
              ].map((caseStudy, index) => (
                <div key={index} className="bg-white rounded-lg shadow-lg p-8">
                  <div className="mb-6">
                    <span className="inline-block bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-medium">
                      {caseStudy.industry}
                    </span>
                  </div>
                  <h3 className="text-xl font-serif text-gray-900 mb-4">課題</h3>
                  <p className="text-gray-600 mb-6">{caseStudy.challenge}</p>
                  
                  <h3 className="text-xl font-serif text-gray-900 mb-4">解決策</h3>
                  <p className="text-gray-600 mb-6">{caseStudy.solution}</p>
                  
                  <h3 className="text-xl font-serif text-gray-900 mb-4">成果</h3>
                  <p className="text-gray-600 mb-4">{caseStudy.result}</p>
                  
                  <div className="text-sm text-amber-700 font-medium">
                    プロジェクト期間：{caseStudy.duration}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Case Studies */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-serif text-gray-900 mb-4">
                コンサルティングの特徴
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                {
                  title: '現場に寄り添う姿勢',
                  description: '机上の理論ではなく、実際の現場の状況を深く理解し、その企業様に最適なソリューションをご提案します。執事文化の研究を通じて学んだ、細やかな観察力と先読みの力を活かします。',
                  icon: 'ri-hand-heart-line'
                },
                {
                  title: '継続的な改善支援',
                  description: '一時的な改善で終わらせず、継続的な成長をサポートする体制を構築します。共に学び、共に成長する姿勢を大切にしています。',
                  icon: 'ri-refresh-line'
                },
                {
                  title: '実践的なアプローチ',
                  description: '理論だけでなく、現場ですぐに活用できる実践的なノウハウをお伝えします。お客様の課題に合わせたカスタマイズを行います。',
                  icon: 'ri-tools-line'
                },
                {
                  title: '長期的な視点',
                  description: '短期的な成果だけでなく、持続可能な成長を見据えたコンサルティングを行います。企業文化の醸成を重視しています。',
                  icon: 'ri-line-chart-line'
                }
              ].map((feature, index) => (
                <div key={index} className="bg-white rounded-lg shadow-lg p-8">
                  <div className="w-16 h-16 flex items-center justify-center mb-6 bg-amber-100 rounded-full">
                    <i className={`${feature.icon} text-2xl text-amber-700`}></i>
                  </div>
                  <h3 className="text-xl font-serif text-gray-900 mb-4">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-amber-700 to-amber-800">
          <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-serif text-white mb-6">
              サービス品質向上のご相談
            </h2>
            <p className="text-xl text-amber-100 mb-8 leading-relaxed">
              まずは現状の課題をお聞かせください。最適なソリューションをご提案いたします
            </p>
            <Link
              to="/contact"
              className="btn-gold inline-block bg-white text-amber-800 px-8 py-4 rounded-lg font-medium hover:bg-amber-50 transition-colors whitespace-nowrap"
            >
              無料相談のお申し込み
            </Link>
          </div>
        </section>
      </div>
    </PageLayout>
  );
};

export default Consulting;
